import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from '@iconify/react';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import { Box, Button, Checkbox, IconButton, Radio, Switch, Tooltip, Typography, useTheme } from '@mui/material';
import { AddCircle } from 'iconsax-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Asterisk } from '@ui/assets/custom-icons/star-icon';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import ControlledFilledTextField from '@ui/components/common/TextField/ControlledFilledTextField';
import { FORM_FIELD_SCHEMA } from '@ui/components/Cards/QuestionCard';
import EditableField from '../EditableField';
import FormFieldsMenu from '../FormFieldsMenu';
import { ADDITIONAL_ATTENDEE_ALLOWED_SUB_FIELD_TYPES, isOptionBasedAttendeeSubField } from '../../presets/additionalAttendeesPreset';
const getSubFieldTypeDisplayLabel = (subField, t) => {
    const { fieldParent, fieldChild, fieldType } = subField;
    const parentSchema = fieldParent ? FORM_FIELD_SCHEMA[fieldParent] : undefined;
    const fallbackSchema = !parentSchema
        ? Object.values(FORM_FIELD_SCHEMA).find((item) => item.fieldType === fieldType)
        : undefined;
    const schema = parentSchema || fallbackSchema;
    const displayParent = schema ? t(schema.displayParentKey) : fieldParent || '';
    const displayChild = fieldChild && FORM_FIELD_SCHEMA[fieldChild]
        ? t(FORM_FIELD_SCHEMA[fieldChild].displayParentKey)
        : fieldChild || '';
    if (displayParent && displayChild) {
        return `${displayParent} → ${displayChild}`;
    }
    return displayParent || t('events.additionalAttendeesPreset.selectFieldType');
};
const getMenuItemIdByFieldType = (fieldType) => {
    const schemaEntry = Object.values(FORM_FIELD_SCHEMA).find((item) => item.fieldType === fieldType);
    return schemaEntry?.menuItemId || 'text';
};
const transparentEditableFieldSx = (theme) => ({
    editableFieldSx: {
        width: '100%',
        border: 'none',
        outline: 'none',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        p: 0,
        color: theme.palette.text.primary,
        ...theme.typography.body2,
        '& .MuiFilledInput-root': {
            boxShadow: 'none',
            background: 'transparent',
            '&.Mui-focused': {
                backgroundColor: 'transparent'
            },
            '&:hover': {
                backgroundColor: 'transparent'
            },
            '&:before, &:after': {
                display: 'none'
            }
        }
    },
    inputSx: {
        '& input': {
            ...theme.typography.body2,
            fontWeight: 400,
            color: theme.palette.text.primary,
            padding: '0px',
            '&::placeholder': {
                ...theme.typography.body2,
                opacity: 1,
                color: theme.palette.text.disabled
            }
        }
    }
});
const PillMenuButton = ({ label, disabled, onOpen }) => {
    const theme = useTheme();
    return (_jsx(Button, { variant: 'soft', size: 'medium', disabled: disabled, onClick: (e) => onOpen(e.currentTarget), sx: {
            backgroundColor: theme.palette.common.grey8,
            color: theme.palette.text.primary,
            borderRadius: 2,
            textTransform: 'none',
            minHeight: 40,
            px: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexShrink: 0,
            maxWidth: { xs: '100%', md: 280 },
            '&:hover': {
                backgroundColor: theme.palette.grey[50]
            }
        }, endIcon: _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill', fontSize: '20px', color: theme.palette.primary.darker }), children: _jsx(Box, { sx: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ...theme.typography.body2,
                fontWeight: 400
            }, children: label }) }));
};
const SortableSubFieldRow = ({ subField, fieldId, canEdit = true, disabledByResponses = false, formFields = [], onSubFieldLabelUpdate, onSubFieldTypeChange, onSubFieldRemove, onSubFieldRequiredChange, onSubFieldOptionAdd, onSubFieldOptionRemove, onSubFieldOptionLabelUpdate, totalSubFields }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [typeMenuAnchor, setTypeMenuAnchor] = useState(null);
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: subField.id
    });
    const fieldStyles = transparentEditableFieldSx(theme);
    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        touchAction: 'none'
    };
    const isDropdown = subField.fieldType === 'dropdown';
    const isToggle = subField.fieldType === 'toggle';
    const hasOptions = isOptionBasedAttendeeSubField(subField.fieldType);
    const isLocked = Boolean(subField.role);
    const showRemove = canEdit;
    const canRemove = canEdit && !disabledByResponses && totalSubFields > 1 && !isLocked;
    const removeDisabledTooltip = isLocked
        ? t('events.additionalAttendeesPreset.mandatoryFieldTooltip')
        : disabledByResponses
            ? t('common.deleteFieldHasResponses')
            : t('events.additionalAttendeesPreset.cannotRemoveLastSubField');
    const canChangeType = canEdit && !isLocked;
    const canRemoveOption = canEdit && !disabledByResponses && !isToggle;
    return (_jsx(Box, { ref: setNodeRef, style: style, children: _jsxs(Box, { sx: {
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                py: 1,
                pl: isDropdown ? 1.5 : 0,
                borderLeft: isDropdown ? `2px solid ${theme.palette.grey[300]}` : 'none',
                background: 'transparent'
            }, children: [_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        background: 'transparent'
                    }, children: [canEdit && (_jsx(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'grab',
                                color: theme.palette.grey[600],
                                flexShrink: 0
                            }, ...attributes, ...listeners, children: _jsx(Icon, { icon: 'material-symbols:drag-indicator', fontSize: '16px' }) })), _jsx(Box, { sx: { flex: 1, minWidth: { xs: '100%', md: 120 } }, children: _jsx(EditableField, { bg: 'light', value: subField.label, onConfirmValue: (newLabel) => onSubFieldLabelUpdate(fieldId, subField.id, newLabel), onResetValue: () => onSubFieldLabelUpdate(fieldId, subField.id, subField.label), editableFieldSx: fieldStyles.editableFieldSx, inputSx: fieldStyles.inputSx }) }), isLocked && (_jsx(Tooltip, { title: t('events.additionalAttendeesPreset.mandatoryFieldTooltip'), children: _jsx(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: theme.palette.grey[600],
                                    flexShrink: 0
                                }, children: _jsx(Icon, { icon: 'eva:lock-outline', fontSize: '16px' }) }) })), _jsx(PillMenuButton, { label: getSubFieldTypeDisplayLabel(subField, t), disabled: !canChangeType, onOpen: setTypeMenuAnchor }), typeMenuAnchor && canChangeType && (_jsx(FormFieldsMenu, { anchorRef: typeMenuAnchor, anchorPosition: 'right', onClose: () => setTypeMenuAnchor(null), formFields: formFields, restrictToTypes: ADDITIONAL_ATTENDEE_ALLOWED_SUB_FIELD_TYPES, disableUniquenessChecks: true, selectedItemId: getMenuItemIdByFieldType(subField.fieldType), onSelect: ({ parent, child, type }) => {
                                onSubFieldTypeChange(fieldId, subField.id, type, parent.label, child?.label);
                                setTypeMenuAnchor(null);
                            } })), _jsx(Button, { className: 'required-btn', variant: 'soft', size: 'medium', disabled: !canEdit, onClick: () => onSubFieldRequiredChange(fieldId, subField.id, !subField.required), sx: {
                                backgroundColor: theme.palette.common.grey8,
                                color: subField.required ? theme.palette.primary.darker : theme.palette.text.disabled,
                                border: subField.required ? `1px solid ${theme.palette.primary.darker}` : 'none',
                                borderRadius: 1.5,
                                padding: '6px 12px',
                                fontSize: '14px',
                                fontWeight: 500,
                                textTransform: 'none',
                                minHeight: 40,
                                flexShrink: 0,
                                '&:hover': {
                                    backgroundColor: theme.palette.grey[50],
                                    borderColor: theme.palette.grey[400]
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: theme.palette.action.disabledBackground,
                                    color: theme.palette.action.disabled,
                                    border: `1px solid ${theme.palette.grey[700]}`
                                }
                            }, startIcon: _jsx(Box, { sx: {
                                    width: 22,
                                    height: 22,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }, children: _jsx(Asterisk, { color: subField.required ? theme.palette.primary.darker : theme.palette.text.disabled }) }), children: t('events.formFieldsMenu.required') }), showRemove && (_jsx(Tooltip, { title: canRemove ? '' : removeDisabledTooltip, children: _jsx("span", { children: _jsx(IconButton, { size: 'small', disabled: !canRemove, onClick: () => onSubFieldRemove(fieldId, subField.id), sx: {
                                        border: 'none',
                                        background: 'transparent',
                                        flexShrink: 0,
                                        color: theme.palette.text.primary,
                                        '&.Mui-disabled': {
                                            color: theme.palette.action.disabled,
                                            opacity: 0.55
                                        }
                                    }, children: _jsx(Icon, { icon: 'eva:close-fill', fontSize: '18px', color: 'currentColor' }) }) }) }))] }), hasOptions && (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, pl: 0.5 }, children: [(subField.options || []).map((option) => (_jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }, children: [subField.fieldType === 'checkbox-group' && (_jsx(Checkbox, { disabled: true, size: 'small', sx: { p: 0.5, flexShrink: 0 } })), subField.fieldType === 'multi-select' && (_jsx(Checkbox, { disabled: true, size: 'small', sx: { p: 0.5, flexShrink: 0 } })), subField.fieldType === 'radio' && (_jsx(Radio, { disabled: true, size: 'small', sx: { p: 0.5, flexShrink: 0 } })), subField.fieldType === 'choice' && (_jsx(Box, { sx: {
                                        width: 20,
                                        height: 20,
                                        borderRadius: 1,
                                        border: `1px solid ${theme.palette.grey[400]}`,
                                        flexShrink: 0
                                    } })), isToggle ? (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }, children: [_jsx(Switch, { checked: Number(option.value) === 1, disabled: true, size: 'small', sx: {
                                                '& .MuiSwitch-switchBase': {
                                                    left: '-4px'
                                                }
                                            } }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: option.label })] })) : (_jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: _jsx(EditableField, { bg: 'light', value: option.label, onConfirmValue: (newLabel) => onSubFieldOptionLabelUpdate(fieldId, subField.id, option.id, newLabel), onResetValue: () => onSubFieldOptionLabelUpdate(fieldId, subField.id, option.id, option.label), editableFieldSx: fieldStyles.editableFieldSx, inputSx: {
                                            '& input': {
                                                ...theme.typography.body2,
                                                fontWeight: 400,
                                                color: theme.palette.text.secondary,
                                                padding: '0px'
                                            }
                                        } }) })), canRemoveOption && (_jsx(IconButton, { size: 'small', onClick: () => onSubFieldOptionRemove(fieldId, subField.id, option.id), sx: { border: 'none', background: 'transparent' }, children: _jsx(Icon, { icon: 'eva:close-fill', fontSize: '16px', color: theme.palette.text.primary }) }))] }, option.id))), canRemoveOption && (_jsx(Button, { variant: 'text', size: 'small', onClick: () => onSubFieldOptionAdd(fieldId, subField.id), sx: {
                                alignSelf: 'flex-start',
                                color: theme.palette.primary.darker,
                                ...theme.typography.body2,
                                fontWeight: 400,
                                lineHeight: 22 / 14,
                                mt: 0.5
                            }, startIcon: _jsx(AddCircle, { color: theme.palette.primary.darker, fontSize: '20px' }), children: t('common.addMore') }))] }))] }) }));
};
const AdditionalAttendeesField = ({ fieldId, maxAdditionalAttendees, maxAdditionalAttendeesReachedMessage, subFields, formFields = [], canEdit = true, disabledByResponses = false, onMaxAttendeesChange, onMaxAttendeesReachedMessageChange, onSubFieldLabelUpdate, onSubFieldTypeChange, onSubFieldRemove, onSubFieldRequiredChange, onSubFieldsReorder, onSubFieldOptionAdd, onSubFieldOptionRemove, onSubFieldOptionLabelUpdate, onSubFieldAdd }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const sensors = useSensors(useSensor(PointerSensor));
    const [addSubFieldAnchorEl, setAddSubFieldAnchorEl] = useState(null);
    const { control, watch, setValue } = useForm({
        defaultValues: {
            maxAdditionalAttendees,
            maxReachedMessage: maxAdditionalAttendeesReachedMessage ?? ''
        }
    });
    useEffect(() => {
        setValue('maxAdditionalAttendees', maxAdditionalAttendees);
    }, [maxAdditionalAttendees, setValue]);
    useEffect(() => {
        setValue('maxReachedMessage', maxAdditionalAttendeesReachedMessage ?? '', {
            shouldDirty: false
        });
    }, [maxAdditionalAttendeesReachedMessage, setValue]);
    useEffect(() => {
        const subscription = watch((data, { name, type }) => {
            if (type !== 'change')
                return;
            if (name === 'maxAdditionalAttendees') {
                const value = data.maxAdditionalAttendees;
                if (value != null) {
                    onMaxAttendeesChange(fieldId, Number(value));
                }
            }
            if (name === 'maxReachedMessage') {
                onMaxAttendeesReachedMessageChange(fieldId, data.maxReachedMessage ?? '');
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, fieldId, onMaxAttendeesChange, onMaxAttendeesReachedMessageChange]);
    const maxAttendeeSelectOptions = useMemo(() => Array.from({ length: 20 }, (_, index) => ({
        label: String(index + 1),
        value: index + 1
    })), []);
    const sortedSubFields = [...subFields].sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0));
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id)
            return;
        onSubFieldsReorder(fieldId, String(active.id), String(over.id));
    };
    return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, mt: 1, background: 'transparent' }, children: [_jsx(SingleSelect, { name: 'maxAdditionalAttendees', label: t('events.additionalAttendeesPreset.maxAttendeesLabel'), control: control, options: maxAttendeeSelectOptions, bg: 'grey8', disabled: !canEdit, disableClearable: true, placeholder: t('events.additionalAttendeesPreset.maxAttendeesPlaceholder') }), _jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', name: 'maxReachedMessage', rows: 4, multiline: true, control: control, disabled: !canEdit, label: t('events.additionalAttendeesPreset.maxReachedMessageLabel'), labelTooltipText: t('events.additionalAttendeesPreset.maxReachedMessageHelper'), placeholder: t('events.additionalAttendeesPreset.maxReachedMessageDefault') }), _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx(SortableContext, { items: sortedSubFields.map((field) => field.id), strategy: verticalListSortingStrategy, children: _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.5, background: 'transparent' }, children: sortedSubFields.map((subField) => (_jsx(SortableSubFieldRow, { subField: subField, fieldId: fieldId, canEdit: canEdit, disabledByResponses: disabledByResponses, formFields: formFields, totalSubFields: sortedSubFields.length, onSubFieldLabelUpdate: onSubFieldLabelUpdate, onSubFieldTypeChange: onSubFieldTypeChange, onSubFieldRemove: onSubFieldRemove, onSubFieldRequiredChange: onSubFieldRequiredChange, onSubFieldOptionAdd: onSubFieldOptionAdd, onSubFieldOptionRemove: onSubFieldOptionRemove, onSubFieldOptionLabelUpdate: onSubFieldOptionLabelUpdate }, subField.id))) }) }) }), canEdit && (_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', mt: 1 }, children: _jsxs(Box, { position: 'relative', children: [_jsx(Button, { variant: 'white', color: 'primary', size: 'large', disabled: disabledByResponses, endIcon: _jsx(MoreHorizOutlinedIcon, { fontSize: 'small', sx: { color: theme.palette.primary.darker } }), sx: { color: theme.palette.primary.darker }, onClick: (e) => setAddSubFieldAnchorEl(e.currentTarget), children: t('actions.addQuestion') }), addSubFieldAnchorEl && (_jsx(FormFieldsMenu, { anchorRef: addSubFieldAnchorEl, anchorPosition: 'left', onClose: () => setAddSubFieldAnchorEl(null), formFields: formFields, restrictToTypes: ADDITIONAL_ATTENDEE_ALLOWED_SUB_FIELD_TYPES, disableUniquenessChecks: true, onSelect: ({ parent, child, type, description }) => {
                                onSubFieldAdd(fieldId, { parent, child, type, description });
                                setAddSubFieldAnchorEl(null);
                            } }))] }) }))] }));
};
export default AdditionalAttendeesField;
